/**
 * 기구 무대 효과음(물품 목록 4.8.1 §6 — Web Audio 합성 · 파일 · 라이선스 없음). 작게 켜 둔다(교실 소음) · 끄기 단추 하나.
 * 소리는 꾸밈이다 — 판정과 무관하다. AudioContext 는 첫 사용자 동작 뒤에 만든다(자동 재생 정책).
 */
let ctx: AudioContext | null = null;
let muted = false;
const VOLUME = 0.12;

function audio(): AudioContext | null {
  if (muted) return null;
  if (typeof window === "undefined" || typeof window.AudioContext === "undefined") return null;
  ctx ??= new window.AudioContext();
  if (ctx.state === "suspended") void ctx.resume().catch(() => undefined);
  return ctx;
}

function tone(
  hz: number,
  at: number,
  dur: number,
  type: OscillatorType = "sine",
  gain = 1,
  slideTo?: number,
) {
  const a = audio();
  if (!a) return;
  const t0 = a.currentTime + at;
  const o = a.createOscillator();
  const g = a.createGain();
  o.type = type;
  o.frequency.setValueAtTime(hz, t0);
  if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(VOLUME * gain, t0 + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(g).connect(a.destination);
  o.start(t0);
  o.stop(t0 + dur + 0.05);
}

const NOTE = { C5: 523.25, E5: 659.25, G5: 783.99, C6: 1046.5, G4: 392, E4: 329.63 };

export const stageSound = {
  /** 처음 단추를 누를 때 — 소리 준비(자동 재생 정책) */
  unlock() {
    audio();
  },
  /** 별 하나 '딩동댕' */
  star() {
    tone(NOTE.C5, 0, 0.14);
    tone(NOTE.E5, 0.12, 0.14);
    tone(NOTE.G5, 0.24, 0.22);
  },
  /** 별 셋 팡파르 */
  fanfare() {
    [NOTE.C5, NOTE.E5, NOTE.G5].forEach((f, i) => tone(f, i * 0.12, 0.18, "square", 0.6));
    [NOTE.C5, NOTE.E5, NOTE.G5, NOTE.C6].forEach((f) => tone(f, 0.4, 0.7, "triangle", 0.7));
  },
  /** 숨은 보너스 반짝 */
  bonus() {
    [1318.5, 1568, 2093, 2637].forEach((f, i) => tone(f, i * 0.07, 0.16, "sine", 0.5));
  },
  /** 아쉬움 '뾰로롱' */
  oops() {
    tone(NOTE.G4, 0, 0.3, "sine", 0.8, NOTE.E4);
  },
  /** 아이 프로그램의 허브 음(음 이름 → 소리) — 브릭 악기 무대 */
  note(name: string) {
    const hz = noteHz(name);
    if (hz) tone(hz, 0, 0.25, "triangle", 0.8);
  },
  get muted() {
    return muted;
  },
  setMuted(m: boolean) {
    muted = m;
  },
};

const SEMIS: Readonly<Record<string, number>> = {
  C: 0,
  "C#": 1,
  D: 2,
  "D#": 3,
  E: 4,
  F: 5,
  "F#": 6,
  G: 7,
  "G#": 8,
  A: 9,
  "A#": 10,
  B: 11,
};
/** 음 이름(C4 · F#5) → Hz(A4 = 440). 모르면 null */
export function noteHz(name: string): number | null {
  const m = /^([A-G]#?)(-?\d)$/.exec(name);
  if (!m) return null;
  const midi = (Number(m[2]) + 1) * 12 + SEMIS[m[1]!]!;
  return 440 * 2 ** ((midi - 69) / 12);
}
